import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { ReportDetailTable, formatCurrency } from '../../components/ReportsNative';
import { useReportData } from '../../hooks/useReportData';
import { unwrapReportData } from './reportPageUtils';
import { useSourceFileViewer, SourceFileLink } from './useSourceFileViewer';

/**
 * GL drill modal — P&L actual amounts drill to the GL grain. Queries
 * /reportData/glDetail for the clicked account + period (same RLS as the
 * GL Detail page) and reconciles the transaction sum against the P&L cell.
 * P&L leaf signs flip income (×−1), so reconciliation compares magnitudes.
 */
export function GlDrillModal({ drill, onClose, currency }) {
  const query = useReportData('glDetail', drill?.params || null);
  const { openSourceFile, viewer: sourceFileViewer } = useSourceFileViewer();

  useEffect(() => {
    if (!drill) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drill, onClose]);

  const data = unwrapReportData(query.data);
  const rows = useMemo(() => data?.rows || [], [data]);
  const totalRows = data?.totalRows ?? rows.length;
  const sum = useMemo(() => rows.reduce((a, r) => a + (Number(r.amount) || 0), 0), [rows]);
  const complete = rows.length >= totalRows;
  const expected = drill?.expected;
  const reconciles =
    complete && expected !== null && expected !== undefined && Math.abs(Math.abs(sum) - Math.abs(expected)) < 0.01;

  if (!drill) return null;

  const modal = (
    <>
    <div className="rn-modal-backdrop" onClick={onClose}>
      <div
        className="rn-modal rn-modal-wide"
        role="dialog"
        aria-label="GL transactions"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rn-modal-head">
          <h2>{drill.accountLabel}</h2>
          <span className="rn-pill-note">{drill.periodLabel}</span>
          <div className="rn-spacer" />
          <button type="button" className="rn-icon-btn" title="Close" onClick={onClose}>
            <X />
          </button>
        </div>
        <div className="rn-modal-sub">
          {query.isFetching && !data ? (
            'Loading transactions…'
          ) : (
            <>
              {totalRows.toLocaleString()} transaction{totalRows === 1 ? '' : 's'} · sum{' '}
              <b>{formatCurrency(sum, { currency })}</b>
              {expected !== null && expected !== undefined && complete && (
                <span className={`rn-gl-reconcile${reconciles ? '' : ' rn-gl-reconcile-off'}`}>
                  {reconciles
                    ? '✓ reconciles to the P&L cell'
                    : `≠ P&L cell ${formatCurrency(expected, { currency })} (Δ ${formatCurrency(Math.abs(sum) - Math.abs(expected), { currency })})`}
                </span>
              )}
              {!complete && ` · showing first ${rows.length.toLocaleString()}`}
            </>
          )}
        </div>
        <div className="rn-modal-list" style={{ padding: '0 6px' }}>
          <ReportDetailTable
            columns={[
              { key: 'date', label: 'Date', width: 92 },
              { key: 'locationName', label: 'Property' },
              { key: 'vendor', label: 'Vendor' },
              {
                key: 'transactionDesc',
                label: 'Description',
                render: (v, r) => {
                  const text = v || r.description || r.memo || null;
                  if (r.sourceDocumentUrl) {
                    return (
                      <SourceFileLink
                        title="Open the source invoice"
                        onOpen={() => openSourceFile(r.sourceDocumentUrl, 'Invoice')}
                      >
                        {text || 'Invoice'}
                      </SourceFileLink>
                    );
                  }
                  return text || '—';
                },
              },
              { key: 'amount', label: 'Amount', align: 'right', format: 'currency2' },
            ]}
            rows={rows}
            currency={currency}
            loading={query.isFetching && !data}
            maxHeight={420}
            fullscreenControl={false}
            emptyText="No GL transactions for this account in the period."
            footer={{ label: `Sum · ${rows.length.toLocaleString()} rows`, cells: { amount: sum } }}
          />
        </div>
      </div>
    </div>
    {/* outside the backdrop so viewer clicks can't bubble into onClose */}
    {sourceFileViewer}
    </>
  );

  return createPortal(modal, document.body);
}

export default GlDrillModal;
